import React from "react"
import { useParams } from "react-router-dom"
import { useShallow } from "zustand/react/shallow"
import Loader from "components/Loading/Loading"
import parse from "html-react-parser"

// assets
import PRSlogo from "./RJS/assets/prs-logo.jpg"
import EUlogo from "./RJS/assets/eudractFlag.png"

// RJS
import RJPreviewForm from "./RJS/components/RJPreviewForm"
import Locations from "./RJS/components/Locations/Locations"

// components
import Comments from "./RJS/components/Comments/Comments"
import { formSchemas, previewFormSchemas } from "constants/authoring"

// apis
import { useGetAuthoringStudy } from "api/hooks/studies/useAuthoringApi"
import { useGetStudy } from "api/hooks/studies/useStudiesApi"
import { useGetAuthoringVersion } from "api/hooks/studies/useAuthoringApi"

// hooks
import { useGetSiteManagementLocations } from "api/hooks/siteManagement/useSiteManagementApi"
import useAuthoringViewStore from "../../hooks/stores/useAuthoringViewStore"
import useAuthoringVersionStore from "../../hooks/stores/useAuthoringVersionStore"

// unique schema
import { ctisResultsDataSchema } from "../../RJS/schemas/dataSchemas/results/ctisResultsDataSchema"
import { ctisResultsUiSchema } from "../../RJS/schemas/uiSchemas/results/ctisResultsUiSchema"
import { REGISTRY_PREVIEW_TYPE } from "constants/authoring"
import useAuthoringPreviewActions from "../../hooks/actions/useAuthoringPreviewActions"

// styles
import "styles/authoring-print.scss"
import "./PagePreview.scss"

const PagePreview = (props) => {
  const {
    overrideData,
    authoring,
    forkedStudyData,
    isSubmissionsPage = false,
    setIsPrintReady = () => {},
    printContainerRef,
  } = props
  const { studyId } = useParams()
  const authoringId = authoring.id

  // local state
  const { selectedVersion } = useAuthoringVersionStore(
    useShallow((state) => ({
      selectedVersion: state.selectedVersion,
    }))
  )
  const { onPrsPreview } = useAuthoringPreviewActions()
  const { isLoading: isAuthoringVersionLoading } = useGetAuthoringVersion(authoringId, selectedVersion?.version_num)
  const [isCtgReady, setIsCtgReady] = React.useState(false)
  const {
    formView,
    formSection,
    showNav,
    formWidthClassName,
    isReviewerView,
    fullScreen,
    showPagePreview,
    showUniqueRegistryPreviewType,
    formStatus,
  } = useAuthoringViewStore(
    useShallow((state) => ({
      formView: state.formView,
      formSection: state.formSection,
      showNav: state.showNav,
      formWidthClassName: state.formWidthClassName,
      isReviewerView: state.isReviewerView,
      fullScreen: state.fullScreen,
      showPagePreview: state.showPagePreview,
      showUniqueRegistryPreviewType: state.showUniqueRegistryPreviewType,
      formStatus: state.formStatus,
    }))
  )
  const [currDataSchema, setCurrDataSchema] = React.useState(null)
  const [currUISchema, setCurrUISchema] = React.useState(null)
  const [htmlPreview, setHtmlPreview] = React.useState("")
  const [loadingPrsPreview, setLoadingPrsPreview] = React.useState(false)

  // server state
  const { refetch: refetchAuthoring, isFetching: isAuthoringIsLoading } = useGetAuthoringStudy(authoringId, {
    ["disable-filter"]: !!isReviewerView,
  })
  const { data: study } = useGetStudy(studyId)
  const { data: sites, isLoading: isSitesLoading } = useGetSiteManagementLocations(study?.site_mgmt_study_id)

  React.useEffect(() => {
    if (formView && formSection) {
      let schema = { ...formSchemas[formSection.id].properties[formView.id] }
      if ((formView.id === "ctg" || formView.id === "global") && formSection.id === "protocol") {
        const ctgProtocolSchema = previewFormSchemas["protocol"].properties["ctg"]

        schema.schema = {
          ...schema.schema,
          properties: {
            ...ctgProtocolSchema.schema.properties,
          },
        }

        schema.uiSchema = {
          ...ctgProtocolSchema.uiSchema({ preview: true }),
        }
      } else if (formView.id === "ctg" || formView.id === "global") {
        const ctgProtocolSchema = previewFormSchemas["protocol"].properties["ctg"]
        const ctgResultsSchema = previewFormSchemas["results"].properties["ctg"]

        schema.schema = {
          ...schema.schema,
          properties: {
            ...ctgProtocolSchema.schema.properties,
            ...ctgResultsSchema.schema.properties,
          },
        }

        schema.uiSchema = {
          ...ctgProtocolSchema.uiSchema({ preview: true }),
          ...ctgResultsSchema.uiSchema({ preview: true }),
        }
      } else if (schema.uiSchema) {
        schema.uiSchema = schema.uiSchema({ preview: true })
      }

      if (showUniqueRegistryPreviewType === REGISTRY_PREVIEW_TYPE.ctisResults) {
        const dataSchema = ctisResultsDataSchema
        const uiSchema = ctisResultsUiSchema({ preview: true })
        setCurrDataSchema(dataSchema)
        setCurrUISchema(uiSchema)
      } else if (schema?.schema && schema?.uiSchema) {
        setCurrDataSchema(schema.schema)
        setCurrUISchema(schema.uiSchema)
      } else {
        console.log("ERROR: schema not found:", {
          formView,
          formSection,
        })
      }
    }
  }, [formView, formSection, showUniqueRegistryPreviewType])

  React.useEffect(() => {
    if (formView?.id === "ctg" && authoringId) {
      onGetPrsPreview()
    }
    refetchAuthoring()
  }, [formView?.id, formSection?.id, authoringId])

  React.useEffect(() => {
    if (
      currDataSchema &&
      currUISchema &&
      formView &&
      formSection &&
      isSitesLoading === false &&
      isAuthoringIsLoading === false &&
      isAuthoringVersionLoading === false &&
      authoring.study_data &&
      formStatus === "ready"
    ) {
      const startPrinting = async () => {
        await new Promise((r) => setTimeout(r, 3000))

        if (formView?.id === "ctg") {
          if (isCtgReady === true) {
            setIsPrintReady(true)
          }
        } else {
          setIsPrintReady(true)
        }
      }

      startPrinting()
    }
  }, [
    currDataSchema,
    currUISchema,
    formView,
    formSection,
    isSitesLoading,
    isCtgReady,
    isAuthoringIsLoading,
    isAuthoringVersionLoading,
    authoring,
    formStatus,
  ])

  const onGetPrsPreview = async (downloadFileType = "html") => {
    setLoadingPrsPreview(true)

    const onSettled = async () => {
      setLoadingPrsPreview(false)
      await new Promise((r) => setTimeout(r, 1000))
      setIsCtgReady(true)
    }

    const onSuccess = async (data) => {
      if (data?.html_str) {
        setHtmlPreview(data?.html_str)
        onSettled()
      }
    }

    onPrsPreview({
      authoringId,
      formSection,
      previewType: "html",
      callback: onSuccess,
      onSettled,
    })
  }

  const displayLocations = () => {
    if (formView?.id === "ctg") {
      return true
    }
    return false
  }

  const displayHeader = () => {
    if (formView?.id === "ctg") {
      let title = "Protocol Registration and Results Preview"
      if (formSection.id === "protocol") title = "Protocol Registration"

      return (
        <div className="authoring-page-preview-header">
          <img
            className="authoring-page-preview-header-img"
            src={PRSlogo}
            alt="xogene-prs-logo"
          />
          <div className="authoring-page-preview-header-title">{title}</div>
        </div>
      )
    } else if (formView?.id === "eudra") {
      return (
        <>
          <div
            className="authoring-page-preview-header eudract-header"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              paddingTop: 16,
              paddingBottom: 16,
              marginBottom: 16,
              width: "100%",
              borderTop: "1px solid #000",
              borderBottom: "1px solid #000",
            }}
          >
            <img
              className="authoring-page-preview-header-img"
              src={EUlogo}
              alt="xogene-eudract-logo"
            />
            <div
              className="authoring-page-preview-header-title"
              style={{
                fontSize: 22,
                marginLeft: 16,
              }}
            >
              EU Clinical Trials Register
            </div>
          </div>
        </>
      )
    }

    let title = `${formView?.id ? formView.id.toUpperCase() : "Clinical Trial"} Study`
    if (formView?.id === "eupas") title = "EMA RWD Study"
    else if (formView?.id === "ctis" || showUniqueRegistryPreviewType === REGISTRY_PREVIEW_TYPE.ctisResults)
      title = "CTIS Study"
    else if (formView?.id === "jrct") title = "jRCT Study"

    return (
      <div className="authoring-page-preview-header">
        <div className="authoring-page-preview-header-title">{title}</div>
      </div>
    )

    return null
  }

  const forkedFieldsSnakeCase = React.useMemo(() => {
    const snakeCase = {}
    if (forkedStudyData) {
      Object.keys(forkedStudyData).forEach((registry) => {
        let key = registry

        if (registry === "eudract") key = "eudra"
        snakeCase[key] = {}

        Object.keys(forkedStudyData[registry]).forEach((forkedKey) => {
          let newKey = forkedKey.replaceAll(".", "_")
          snakeCase[key][newKey] = forkedStudyData[registry][forkedKey]
        })
      })
    }

    return snakeCase
  }, [forkedStudyData])

  const overrideDataSnakeCase = React.useMemo(() => {
    const snakeCase = {}

    if (overrideData) {
      Object.keys(overrideData).forEach((overrideKey) => {
        let newKey = overrideKey.replaceAll(".", "_")
        snakeCase[newKey] = overrideData[overrideKey]
      })
    }

    return snakeCase
  }, [overrideData])

  const contextState = {
    authoring,
    formData: authoring?.study_data,
    formDataRef: authoring?.study_data,
    formSchemas: previewFormSchemas,
    formSection,
    formView: formView?.id === "eudra" ? { id: "eudract" } : formView,
    readOnly: true,
    forkedFieldsSnakeCase,
    overrideDataSnakeCase,
  }

  if (loadingPrsPreview || !authoring || !authoring?.study_data) {
    return <Loader />
  }

  const classNames = ["authoring-print-page"]

  if (!isReviewerView) {
    classNames.push(formWidthClassName)
  }

  if (showNav && showPagePreview && htmlPreview === "") {
    classNames.push("auth-form--open-nav")
  }

  if (htmlPreview) {
    classNames.push("prs-remote-preview")

    if (!isReviewerView) {
      classNames.push(formWidthClassName)
    }

    if (fullScreen === false) {
      if (showNav) classNames.push("auth-form--open-nav")
    }
  }

  return (
    <div
      id="authoring-print-page-container"
      className={classNames.join(" ")}
      ref={printContainerRef}
    >
      {htmlPreview ? (
        <>{parse(htmlPreview)}</>
      ) : (
        <>
          {displayHeader()}
          {currDataSchema && currUISchema && (
            <RJPreviewForm
              formData={authoring.study_data}
              schema={currDataSchema}
              uiSchema={currUISchema}
              formContext={contextState}
            />
          )}

          {displayLocations() && (
            <Locations
              sites={sites?.filter((loc) => loc.only_on_ctg !== true)}
              formData={authoring.study_data}
            />
          )}
          {formView?.id !== "global" && isSubmissionsPage !== true && <Comments />}
        </>
      )}
    </div>
  )
}

export default PagePreview
